import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchInterviewRequest,
  fetchQuestionsRequest,
  clearError,
  selectSessionData,
  selectCurrentQuestion,
  selectProgress,
  selectIsInterviewStarted,
  selectIsInterviewComplete,
  selectLoading,
  selectError,
  selectConsent,
  selectSessionExpired,
} from "@store/slices/interviewSlice";

import Header from "@components/Layout/Header";
import ProgressBar from "@components/Interview/ProgressBar";
import QuestionCard from "@components/Interview/QuestionCard";
import ErrorAlert from "@components/UI/ErrorAlert";
import Toast from "@components/UI/Toast";

/**
 * InterviewSession Page
 * Main interview questions page
 * Only accessed after intro + setup completion
 */
const InterviewSession = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use selectors from Redux
  const sessionData = useSelector(selectSessionData);
  const currentQuestion = useSelector(selectCurrentQuestion);
  const progress = useSelector(selectProgress);
  const isInterviewStarted = useSelector(selectIsInterviewStarted);
  const isInterviewComplete = useSelector(selectIsInterviewComplete);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const consent = useSelector(selectConsent);
  const sessionExpired = useSelector(selectSessionExpired);

  const { toast } = useSelector((state) => state.ui);

  // Local state
  const [showLoading, setShowLoading] = useState(false);
  const [questionReady, setQuestionReady] = useState(false);
  const [questionKey, setQuestionKey] = useState(0);

  // Fetch interview data on mount — only if sessionData isn't already loaded.
  // When coming from InterviewSetup, sessionData is already in the store.
  // Fetching again would race against the startInterviewRequest POST and could
  // overwrite isInterviewStarted/consent with stale pre-start values.
  useEffect(() => {
    if (sessionToken && !sessionData) {
      dispatch(fetchInterviewRequest(sessionToken));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionToken]);

  // If user lands here without the interview being started, redirect to intro
  useEffect(() => {
    if (sessionData && !isInterviewStarted) {
      navigate(`/interview/${sessionToken}`, { replace: true });
    }
  }, [sessionData, isInterviewStarted, sessionToken, navigate]);

  // Handle session expiry
  useEffect(() => {
    if (sessionExpired) {
      navigate("/session-expired", { replace: true });
    }
  }, [sessionExpired, navigate]);

  // Handle interview completion
  useEffect(() => {
    if (isInterviewComplete) {
      navigate(`/interview/${sessionToken}/complete`, { replace: true });
    }
  }, [isInterviewComplete, navigate, sessionToken]);

  const handleClearError = () => {
    dispatch(clearError());
  };

  // Check if consent is valid
  const hasConsent = !!(
    consent &&
    consent.termsAccepted === true &&
    typeof consent.termsVersion === "string" &&
    consent.termsVersion.length > 0 &&
    consent.recordingConsent === true &&
    typeof consent.mediaMode === "string" &&
    consent.mediaMode.length > 0
  );

  // Loading animation when question changes
  useEffect(() => {
    if (hasConsent && currentQuestion && !loading) {
      setShowLoading(true);
      setQuestionReady(false);
      setQuestionKey((prev) => prev + 1);

      const timer = setTimeout(() => {
        setShowLoading(false);
        setQuestionReady(true);
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
      setQuestionReady(false);
    }
  }, [hasConsent, currentQuestion?.id, loading]);

  // Show loading while fetching data or waiting for interview start
  if (!sessionData || !isInterviewStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <ProgressBar />

        {/* Main Content - Questions */}
        <div className="mt-8">
          <div className="relative min-h-[300px]">
            {/* Loading Screen */}
            {showLoading && (
              <div
                key={`loading-${questionKey}`}
                className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-95 z-10 rounded-2xl"
              >
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-6" />
                <div className="text-lg font-semibold text-gray-700 animate-pulse">
                  Loading next question...
                </div>
              </div>
            )}

            {/* Question Card */}
            {questionReady && currentQuestion && !showLoading && (
              <div
                key={`question-${questionKey}-${currentQuestion.id}`}
                className="animate-slide-in"
              >
                <QuestionCard
                  question={currentQuestion}
                  sessionToken={sessionToken}
                  progress={progress}
                  answerFormat={consent.mediaMode}
                  mediaPermissionGranted={consent.recordingConsent}
                  companyName={sessionData?.company_name || 'the hiring company'}
                />
              </div>
            )}

            {/* No questions available */}
            {!showLoading && !currentQuestion && (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading questions...</p>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-6">
            <ErrorAlert message={error} onClose={handleClearError} />
          </div>
        )}
      </main>

      {/* Toast Notifications */}
      {toast?.isVisible && <Toast message={toast.message} type={toast.type} />}

      {/* Animation Styles */}
      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slide-in {
          animation: slideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default InterviewSession;